import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText, Divider, ListSubheader, useTheme, TextField } from '@mui/material';
import { KeyboardArrowRight as ChevronRightIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from '@application/utilities/i18n';
import { useSelector } from 'react-redux';
import { getAllUserCompaniesAsync } from '@application/features/company/companyThunk';
import { useAppDispatch } from '@application/store/useStore';
import { Icon } from '@iconify/react';
const UserMenu = ({ user, onMyAccount, companies, selectedCompanyId, onCompanySelect, onGroupChange, onLogout, anchorEl, open, onClose }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [activeSubmenu, setActiveSubmenu] = useState(null);
    const [submenuAnchor, setSubmenuAnchor] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCompanies, setFilteredCompanies] = useState(companies);
    // Languages
    const languages = [
        { textKey: 'language.english', code: 'en', flag: '/assets/images/flags/us-flag.svg' },
        { textKey: 'language.dutch', code: 'nl', flag: '/assets/images/flags/nl-flag.svg' },
        { textKey: 'language.german', code: 'de', flag: '/assets/images/flags/de-flag.svg' }
    ];
    const dispatch = useAppDispatch();
    const selectedUser = useSelector((state) => state.auth.currentUser);
    const initialLang = languages.find((lang) => lang.code === i18n.language) || languages[0];
    const [selectedLang, setSelectedLang] = useState(initialLang);
    // Sync selected language with i18n language changes (e.g., when user's primary language is set on login)
    useEffect(() => {
        const currentLang = languages.find((lang) => lang.code === i18n.language) || languages[0];
        setSelectedLang((prevLang) => {
            if (prevLang.code !== currentLang.code) {
                return currentLang;
            }
            return prevLang;
        });
    }, [i18n.language]);
    const companiesMenu = companies.map((company) => ({
        group: true,
        id: company.id,
        text: company.name
    }));
    const selectedCompany = companies.find((c) => c.id === selectedCompanyId)?.name || '';
    const handleSubmenuOpen = (event, submenuType) => {
        event.stopPropagation();
        setActiveSubmenu(submenuType);
        setSubmenuAnchor(event.currentTarget);
    };
    const handleSubmenuClose = () => {
        setActiveSubmenu(null);
        setSubmenuAnchor(null);
        setSearchTerm('');
    };
    const handleLanguageChange = (lang) => {
        setSelectedLang(lang);
        i18n.changeLanguage(lang.code).then(() => {
            dispatch(getAllUserCompaniesAsync());
        });
        handleSubmenuClose();
        onClose();
    };
    useEffect(() => {
        // Filter companies when the search term changes
        const filtered = companies.filter((company) => company.name.toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredCompanies(filtered);
    }, [searchTerm, companies]);
    // NOTE: Company selection is initialized centrally (Sidebar) to avoid conflicting defaults.
    const menuItems = [
        {
            id: 'language',
            label: t(selectedLang.textKey),
            icon: (_jsx("img", { src: selectedLang.flag, alt: selectedLang.textKey, width: 20, height: 15, style: { borderRadius: 2 } })),
            hasChevron: true,
            submenuType: 'language'
        },
        // {
        //   id: 'group',
        //   label: selectedCompany,
        //   icon: <BusinessIcon sx={{ fontSize: 20, color: theme.palette.grey[700] }} />,
        //   hasChevron: true,
        //   submenuType: 'companies',
        //   onClick: onGroupChange
        // },
        {
            id: 'logout',
            label: t('users.logout'),
            icon: _jsx(Icon, { icon: 'solar:logout-2-bold-duotone', width: '20', height: '20', color: '#FF5630' }),
            onClick: onLogout,
            isLogout: true
        }
    ];
    const getSubmenuItems = (submenuType) => {
        if (submenuType === 'language') {
            return languages.map((lang) => ({
                id: lang.code,
                label: t(lang.textKey),
                onClick: () => handleLanguageChange(lang)
            }));
        }
        if (submenuType === 'companies') {
            // const filteredCompanies = companies.filter((company) =>
            //   //console.log(searchTerm);
            //   company.name.toLowerCase().includes(searchTerm.toLowerCase())
            // );
            return filteredCompanies.map((company) => ({
                id: company.id,
                label: company.name,
                onClick: () => {
                    onCompanySelect(company.id);
                }
            }));
        }
        return [];
    };
    const handleSearchChange = (e) => {
        e.stopPropagation();
        setSearchTerm(e.target.value);
    };
    return (_jsxs(_Fragment, { children: [_jsx(Menu, { anchorEl: anchorEl, open: open, onClose: onClose, anchorOrigin: { vertical: 'top', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: {
                    sx: {
                        minWidth: 250,
                        borderRadius: 2,
                        ml: 3.5,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        '& .MuiMenuItem-root': { px: 2 }
                    }
                }, children: menuItems.map((item, index) => (_jsxs(React.Fragment, { children: [_jsxs(MenuItem, { onClick: (e) => {
                                if (item.hasChevron) {
                                    handleSubmenuOpen(e, item.submenuType);
                                }
                                else {
                                    item.onClick?.();
                                    onClose();
                                }
                            }, sx: {
                                '&:hover': { backgroundColor: item.isLogout ? '#FFF5F2' : theme.palette.grey[50] }
                            }, children: [_jsx(ListItemIcon, { sx: { minWidth: 32 }, children: item.icon }), _jsx(ListItemText, { primary: item.label, sx: {
                                        '& .MuiListItemText-primary': {
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            color: item.isLogout ? '#FF5630' : theme.palette.grey[800]
                                        }
                                    } }), item.hasChevron && (_jsx(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        padding: 0.5,
                                        borderRadius: 0.5,
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                                    }, children: _jsx(ChevronRightIcon, { sx: { fontSize: 20, color: theme.palette.grey[900] } }) }))] }), index < menuItems.length - 1 && _jsx(Divider, { sx: { mx: 1, my: 0.5 } })] }, item.id))) }), _jsx(Menu, { anchorEl: submenuAnchor, open: Boolean(submenuAnchor), onClose: handleSubmenuClose, anchorOrigin: { vertical: 'top', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, disableAutoFocusItem: true, autoFocus: false, PaperProps: {
                    sx: {
                        mt: 0,
                        ml: 1,
                        minWidth: 250,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        '& .MuiMenuItem-root': { px: 2 }
                    }
                }, children: activeSubmenu && (_jsxs(_Fragment, { children: [_jsx(ListSubheader, { sx: {
                                fontSize: '12px',
                                fontWeight: 600,
                                color: theme.palette.grey[500],
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                px: 2,
                                py: 1,
                                position: 'sticky',
                                top: 0,
                                backgroundColor: theme.palette.background.paper
                            }, children: activeSubmenu === 'language' ? t('common.selectLanguage') : t('common.selectGroup') }), _jsx(Divider, { sx: { mx: 1, my: 0.5 } }), activeSubmenu === 'companies' && (_jsx(Box, { sx: { mx: 2, my: 1 }, children: _jsx(TextField, { fullWidth: true, placeholder: t('common.searchCompanies'), variant: 'outlined', size: 'small', value: searchTerm, onChange: handleSearchChange, inputRef: inputRef, onKeyDown: (e) => e.stopPropagation() }) })), getSubmenuItems(activeSubmenu).map((subItem, index) => (_jsxs(React.Fragment, { children: [_jsxs(MenuItem, { onClick: () => {
                                        subItem.onClick?.();
                                        handleSubmenuClose();
                                        onClose();
                                    }, selected: activeSubmenu === 'language' && subItem.id === selectedLang.code, sx: { '&:hover': { backgroundColor: theme.palette.grey[50] } }, children: [activeSubmenu === 'language' && (_jsx(ListItemIcon, { children: _jsx("img", { src: languages.find((l) => l.code === subItem.id)?.flag, alt: subItem.label, width: 20, height: 15, style: { borderRadius: 2 } }) })), _jsx(ListItemText, { primary: subItem.label, sx: {
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    color: theme.palette.grey[800]
                                                }
                                            } })] }), index < getSubmenuItems(activeSubmenu).length - 1 && (_jsx(Divider, { sx: { mx: 1, my: 0.5 } }))] }, subItem.id)))] })) })] }));
};
export default UserMenu;
